import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import './MeetingsList.css';

const API_BASE = '/api';
const headers = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });
const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
const DOW = ['SUN', 'MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT'];

const ym = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
const parseDate = (s) => new Date(s + 'T00:00:00');
const weekOfMonth = (s) => Math.ceil(parseDate(s).getDate() / 7);
const monthLabel = (m) => { const [y, mo] = m.split('-'); return `${MONTHS[+mo - 1]} ${y}`; };
const titleDow = (i) => DOW[i][0] + DOW[i].slice(1).toLowerCase();
const dateLabel = (s) => { const d = parseDate(s); return `${titleDow(d.getDay())}, ${d.getDate()} ${MONTHS[d.getMonth()].slice(0, 3)}`; };

// Time as hour/minute/AM-PM dropdowns (no free text). Composes to "10:00AM".
const HOURS = Array.from({ length: 12 }, (_, i) => String(i + 1));
const MINUTES = ['00', '05', '10', '15', '20', '25', '30', '35', '40', '45', '50', '55'];
const parseTime = (s) => {
  const m = /^(\d{1,2}):(\d{2})\s*(AM|PM)$/i.exec((s || '').trim());
  return m ? { hour: String(+m[1]), minute: m[2], ampm: m[3].toUpperCase() } : { hour: '10', minute: '00', ampm: 'AM' };
};
const composeTime = (hour, minute, ampm) => `${hour}:${minute}${ampm}`;
function timeDropdowns(value, onChange) {
  const t = parseTime(value);
  return (
    <span className="ml-time-edit">
      <select value={t.hour} onChange={e => onChange(composeTime(e.target.value, t.minute, t.ampm))}>
        {HOURS.map(h => <option key={h} value={h}>{h}</option>)}
      </select>
      <span className="ml-colon">:</span>
      <select value={t.minute} onChange={e => onChange(composeTime(t.hour, e.target.value, t.ampm))}>
        {MINUTES.map(mn => <option key={mn} value={mn}>{mn}</option>)}
      </select>
      <select value={t.ampm} onChange={e => onChange(composeTime(t.hour, t.minute, e.target.value))}>
        <option value="AM">AM</option>
        <option value="PM">PM</option>
      </select>
    </span>
  );
}

// "This week" = the meeting's date is within today's calendar week (Sun–Sat).
function isThisWeek(s) {
  const d = parseDate(s);
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const start = new Date(today); start.setDate(today.getDate() - today.getDay());
  const end = new Date(start); end.setDate(start.getDate() + 6);
  return d >= start && d <= end;
}

function fmtEdited(ts) {
  if (!ts) return null;
  const d = new Date(ts);
  if (Number.isNaN(d.getTime())) return null;
  const mins = Math.round((Date.now() - d.getTime()) / 60000);
  if (mins < 2) return 'just now';
  if (mins < 60) return `${mins} min ago`;
  return d.toLocaleString('en-MY', { day: 'numeric', month: 'short', hour: 'numeric', minute: '2-digit', hour12: true });
}

export default function MeetingsList() {
  const { isStaff } = useAuth();
  const navigate = useNavigate();
  const [meetings, setMeetings] = useState([]);
  const [error, setError] = useState('');
  const [month, setMonth] = useState(ym(new Date()));
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({ meeting_date: '', meeting_time: '10:00AM', title: 'Sales Meeting' });
  const [editId, setEditId] = useState(null);
  const [editForm, setEditForm] = useState({ meeting_date: '', meeting_time: '', title: '' });
  const [targetsOpen, setTargetsOpen] = useState(false);
  const [targetPeople, setTargetPeople] = useState([]);
  const [targetDraft, setTargetDraft] = useState({});
  const [capturedDraft, setCapturedDraft] = useState({});
  const [targetsLoading, setTargetsLoading] = useState(false);
  const [targetsSaving, setTargetsSaving] = useState(false);
  const [targetsError, setTargetsError] = useState('');
  const [targetsSaved, setTargetsSaved] = useState(false);

  const load = useCallback(async () => {
    try { const r = await axios.get(`${API_BASE}/meetings`, { headers: headers() }); setMeetings(r.data.meetings); }
    catch (e) { setError('Failed to load meetings'); }
  }, []);
  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (!targetsOpen) return undefined;
    let active = true;
    setTargetsLoading(true);
    setTargetsError('');
    setTargetsSaved(false);
    setTargetPeople([]);
    axios.get(`${API_BASE}/meetings/targets?month=${month}`, { headers: headers() })
      .then(response => {
        if (!active) return;
        const people = response.data.people || [];
        setTargetPeople(people);
        setTargetDraft(Object.fromEntries(people.map(person =>
          [person.salesperson_id, person.target == null ? '' : String(person.target)])));
        setCapturedDraft(Object.fromEntries(people.map(person =>
          [person.salesperson_id, person.captured_target == null ? '' : String(person.captured_target)])));
      })
      .catch(e => { if (active) setTargetsError(e.response?.data?.error || 'Failed to load targets'); })
      .finally(() => { if (active) setTargetsLoading(false); });
    return () => { active = false; };
  }, [targetsOpen, month]);

  const saveTargets = async () => {
    const updates = [];
    const amountOf = raw => String(raw ?? '').trim() === '' ? null : Number(raw);
    const valid = value => value === null || (Number.isFinite(value) && value >= 0 &&
      value <= 9999999999.99 && Math.abs(value * 100 - Math.round(value * 100)) <= 0.000001);
    for (const person of targetPeople) {
      const target = amountOf(targetDraft[person.salesperson_id]);
      if (!valid(target)) {
        setTargetsError(`Enter a valid delivered RM target for ${person.name}`);
        return;
      }
      const update = { salesperson_id: person.salesperson_id };
      if (target !== person.target) update.target = target;
      if (person.tracks_bookings) {
        const capturedTarget = amountOf(capturedDraft[person.salesperson_id]);
        if (!valid(capturedTarget)) {
          setTargetsError(`Enter a valid captured RM target for ${person.name}`);
          return;
        }
        if (capturedTarget !== person.captured_target) update.captured_target = capturedTarget;
      }
      if (Object.keys(update).length > 1) updates.push(update);
    }
    if (!updates.length) return;
    setTargetsSaving(true);
    setTargetsError('');
    setTargetsSaved(false);
    try {
      const response = await axios.put(`${API_BASE}/meetings/targets`,
        { month, targets: updates }, { headers: headers() });
      const people = response.data.people || [];
      setTargetPeople(people);
      setTargetDraft(Object.fromEntries(people.map(person =>
        [person.salesperson_id, person.target == null ? '' : String(person.target)])));
      setCapturedDraft(Object.fromEntries(people.map(person =>
        [person.salesperson_id, person.captured_target == null ? '' : String(person.captured_target)])));
      setTargetsSaved(true);
    } catch (e) {
      setTargetsError(e.response?.data?.error || 'Failed to save targets');
    } finally {
      setTargetsSaving(false);
    }
  };

  const shiftMonth = (delta) => {
    const [y, mo] = month.split('-').map(Number);
    setMonth(ym(new Date(y, mo - 1 + delta, 1)));
  };

  const create = async () => {
    setError('');
    if (!form.meeting_date) { setError('Pick a date.'); return; }
    try { const r = await axios.post(`${API_BASE}/meetings`, form, { headers: headers() }); setCreating(false); navigate(`/meetings/${r.data.meeting.id}`); }
    catch (e) { setError(e.response?.data?.error || 'Create failed'); }
  };

  const remove = async (e, m) => {
    e.stopPropagation();
    if (!window.confirm(`Delete meeting "${m.title} - ${m.meeting_date}"? This removes its sections and deals.`)) return;
    try { await axios.delete(`${API_BASE}/meetings/${m.id}`, { headers: headers() }); load(); }
    catch (err) { setError(err.response?.data?.error || 'Delete failed'); }
  };

  const startEdit = (e, m) => {
    e.stopPropagation(); setError('');
    setEditId(m.id);
    setEditForm({ meeting_date: m.meeting_date, meeting_time: m.meeting_time || '', title: m.title || 'Sales Meeting' });
  };
  const saveEdit = async (e) => {
    e.stopPropagation(); setError('');
    if (!editForm.meeting_date) { setError('Pick a date.'); return; }
    try { await axios.put(`${API_BASE}/meetings/${editId}`, editForm, { headers: headers() }); setEditId(null); load(); }
    catch (err) { setError(err.response?.data?.error || 'Update failed'); }
  };

  const monthMeetings = meetings
    .filter(m => (m.meeting_date || '').slice(0, 7) === month)
    .sort((a, b) => (a.meeting_date < b.meeting_date ? -1 : 1));

  return (
    <div className="meetings-list">
      <div className="ml-top">
        <h2 className="ml-title">Sales Meetings</h2>
        <div className="ml-mtoggle">
          <button onClick={() => shiftMonth(-1)} aria-label="Previous month">‹</button>
          <span className="ml-mlbl">{monthLabel(month)}</span>
          <button onClick={() => shiftMonth(1)} aria-label="Next month">›</button>
        </div>
        {isStaff && <button className="ml-target-toggle" type="button"
          aria-expanded={targetsOpen} onClick={() => setTargetsOpen(open => !open)}>Monthly targets</button>}
        <div className="ml-spacer" />
        {isStaff && !creating && <button className="ml-newbtn" onClick={() => setCreating(true)}>New meeting</button>}
      </div>

      {targetsOpen && <section className="ml-target-panel" aria-label="Monthly targets editor">
        <div className="ml-target-head">
          <div><h3>Monthly targets · {monthLabel(month)}</h3>
            <p>Targets shown in Sales Meetings. Changing these does not change salary or commission. Frozen meetings keep their saved figures.</p></div>
          <button type="button" className="ml-btn" onClick={() => setTargetsOpen(false)}>Close</button>
        </div>
        {targetsError && <div className="ml-error" role="alert">{targetsError}</div>}
        {targetsLoading ? <p className="ml-target-muted">Loading targets…</p> : <>
          <table className="ml-target-table"><thead><tr><th>Salesperson</th><th>Captured target (RM)</th><th>Delivered target (RM)</th><th>Delivered source</th></tr></thead>
            <tbody>{targetPeople.map(person => <tr key={person.salesperson_id}>
              <td>{person.name}</td>
              <td>{person.tracks_bookings ? <input type="number" min="0" step="0.01"
                aria-label={`${person.name} captured target (RM)`}
                value={capturedDraft[person.salesperson_id] ?? ''} placeholder="No target"
                onChange={e => { setCapturedDraft(draft => ({ ...draft, [person.salesperson_id]: e.target.value })); setTargetsSaved(false); }} />
                : <span className="ml-target-muted">—</span>}</td>
              <td><input type="number" min="0" step="0.01" aria-label={`${person.name} ${person.tracks_bookings ? 'delivered ' : ''}target (RM)`}
                value={targetDraft[person.salesperson_id] ?? ''} placeholder="No target"
                onChange={e => { setTargetDraft(draft => ({ ...draft, [person.salesperson_id]: e.target.value })); setTargetsSaved(false); }} /></td>
              <td className="ml-target-muted">{person.source === 'commission' ? 'Current default' : person.source === 'meeting' ? 'Meeting target' : 'Not set'}</td>
            </tr>)}</tbody></table>
          {!targetPeople.length && <p className="ml-target-muted">No salespeople for this month.</p>}
          <div className="ml-target-actions">
            {targetsSaved && <span className="ml-target-saved" role="status">Targets saved</span>}
            <button type="button" className="ml-btn primary" disabled={targetsSaving || !targetPeople.length}
              onClick={saveTargets}>{targetsSaving ? 'Saving…' : 'Save targets'}</button>
          </div>
        </>}
      </section>}

      {error && <div className="ml-error">{error}</div>}
      {creating && (
        <div className="ml-create">
          <input type="date" value={form.meeting_date} onChange={e => setForm({ ...form, meeting_date: e.target.value })} />
          {timeDropdowns(form.meeting_time, v => setForm({ ...form, meeting_time: v }))}
          <input type="text" placeholder="Title" value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} />
          <button className="ml-btn primary" onClick={create}>Create</button>
          <button className="ml-btn" onClick={() => setCreating(false)}>Cancel</button>
        </div>
      )}

      <table className="ml-table">
        <thead>
          <tr>
            <th>Date</th>
            <th>Week</th>
            <th>Time</th>
            <th>Last edited</th>
            <th className="ml-r" />
          </tr>
        </thead>
        <tbody>
          {monthMeetings.map(m => {
            const now = isThisWeek(m.meeting_date);
            const edited = fmtEdited(m.last_edited_at);
            const label = dateLabel(m.meeting_date);
            const editing = editId === m.id;

            if (editing) {
              return (
                <tr key={m.id} className="ml-editing-row">
                  <td colSpan={5}>
                    <div className="ml-edit" onClick={e => e.stopPropagation()}>
                      <input type="date" value={editForm.meeting_date} onChange={e => setEditForm({ ...editForm, meeting_date: e.target.value })} />
                      {timeDropdowns(editForm.meeting_time, v => setEditForm({ ...editForm, meeting_time: v }))}
                      <input type="text" placeholder="Title" value={editForm.title} onChange={e => setEditForm({ ...editForm, title: e.target.value })} />
                      <div className="ml-edit-actions">
                        <button className="ml-btn primary" onClick={saveEdit}>Save</button>
                        <button className="ml-btn" onClick={e => { e.stopPropagation(); setEditId(null); }}>Cancel</button>
                      </div>
                    </div>
                  </td>
                </tr>
              );
            }

            return (
              <tr
                key={m.id}
                className={now ? 'on' : ''}
                onClick={() => { if (editId !== m.id) navigate(`/meetings/${m.id}`); }}
              >
                <td className="ml-d">
                  {label}
                  {now && <span className="ml-thisweek">· this week</span>}
                </td>
                <td className="ml-w">{weekOfMonth(m.meeting_date)}</td>
                <td className="ml-t">{m.meeting_time || '10:00AM'}</td>
                <td className={'ml-e' + (edited ? '' : ' none')}>
                  {edited
                    ? <>{edited}{m.last_edited_by ? ` · ${m.last_edited_by}` : ''}</>
                    : 'Not edited'}
                </td>
                <td className="ml-acts">
                  {isStaff && (
                    <button className="ml-a" onClick={e => startEdit(e, m)} aria-label={`Edit ${label}`}>Edit</button>
                  )}
                  {isStaff && (
                    <button className="ml-a del" onClick={e => remove(e, m)} aria-label={`Delete ${label}`}>Delete</button>
                  )}
                </td>
              </tr>
            );
          })}
          {monthMeetings.length === 0 && (
            <tr>
              <td colSpan={5} className="ml-empty">No meetings in {monthLabel(month)}.</td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
}
